import { useState, FormEvent } from 'react';

interface LoginPageProps {
  onLogin: (token: string, role: 'admin' | 'viewer') => void;
}

export default function LoginPage({ onLogin }: LoginPageProps) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [loading, setLoading]   = useState(false);

  const apiBase = import.meta.env.VITE_API_URL || 'http://localhost:3001';

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${apiBase}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Anmeldung fehlgeschlagen');
        return;
      }

      const data = await res.json();
      const role: 'admin' | 'viewer' = data.role === 'admin' ? 'admin' : 'viewer';
      onLogin(data.token, role);
    } catch {
      setError('Server nicht erreichbar');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-surface-bg bg-grid flex items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Logo / Titel */}
        <div className="text-center mb-8">
          <img
            src="/fotobox-team-logo.png"
            alt="Fotobox Team"
            className="h-14 w-auto object-contain mx-auto mb-5"
          />
          <h1 className="font-raleway font-thin text-xl uppercase tracking-[0.2em] text-ink-primary">
            STATUS DASHBOARD
          </h1>
          <p className="text-ink-muted text-sm mt-1">Bitte anmelden um fortzufahren</p>
        </div>

        {/* Login-Formular */}
        <div className="rounded-2xl border border-surface-border bg-white p-6 shadow-sm">
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <label className="text-ink-muted text-xs font-medium uppercase tracking-wider">
                Benutzername
              </label>
              <input
                type="text"
                autoComplete="username"
                value={username}
                onChange={e => setUsername(e.target.value)}
                required
                className="bg-surface-subtle border border-surface-border rounded-xl px-3 py-2.5 text-sm text-ink-primary
                           placeholder:text-ink-faint focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                           transition-colors"
                placeholder="admin"
              />
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-ink-muted text-xs font-medium uppercase tracking-wider">
                Passwort
              </label>
              <input
                type="password"
                autoComplete="current-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                required
                className="bg-surface-subtle border border-surface-border rounded-xl px-3 py-2.5 text-sm text-ink-primary
                           placeholder:text-ink-faint focus:outline-none focus:border-brand-green focus:ring-2 focus:ring-brand-green/20
                           transition-colors"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <div className="rounded-xl bg-brand-red-light border border-brand-red-mid px-3 py-2">
                <p className="text-brand-red text-sm">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="mt-1 bg-brand-green hover:bg-brand-green/90 disabled:opacity-50 disabled:cursor-not-allowed
                         text-white font-semibold rounded-xl px-4 py-2.5 text-sm
                         transition-colors duration-150 shadow-sm"
            >
              {loading ? 'Anmelden…' : 'Anmelden'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
